// ─── the provider catalog ────────────────────────────────────────────────────
// `GET /v1/providers` is the service's own list, so the picker can never go stale
// and the app hardcodes no provider names. Each entry may report whether it is
// *configured* — a cloud provider with no key is listed, dimmed, with the reason,
// because a missing key is exactly the failure the boss hits first and hiding it
// would leave a dead mic with no explanation.
//
// Everything here is pure so the shapes and the fallbacks are unit-tested; the one
// impure function is the fetch, which tolerates an older service that answers a
// bare array — or 404s entirely.

import type { AudioFormat, Placement } from '@shared/protocol.ts'
import { DEPLOY_SECRET_PREFIX } from '@shared/session.ts'

export interface ProviderInfo {
  id: string
  placements: Placement[]
  status: 'ready' | 'planned'
  /** `undefined` = the service does not report it; treat as usable. */
  configured?: boolean
  /** Worker secrets this provider still needs. The service names them; we print them. */
  missingSecrets?: string[]
  summary?: string
  /** What the provider actually speaks — qwen listens at 16 kHz, OpenAI at 24 kHz. */
  audio?: { input: AudioFormat; output: AudioFormat }
}

/** Can a session actually be opened on this provider right now? */
export function usable(p: ProviderInfo): boolean {
  return p.status === 'ready' && p.configured !== false
}

/** What the picker prints under a provider's name. */
export function describe(p: ProviderInfo): string {
  if (p.status === 'planned') return 'declared but not implemented yet'
  if (p.configured === false) return `installed, but ${needs(p)}`
  return p.summary ?? `runs ${p.placements.join(' or ')}-side`
}

/**
 * The missing secrets under the name the boss actually pastes them as. The Worker
 * knows them as `OPENAI_API_KEY`; the GitHub secret and the deploy workflow use the
 * `VOICEBOX_` prefix, and that prefix belongs to the contract, not to us.
 */
export function deploySecretNames(p: ProviderInfo): string[] {
  return (p.missingSecrets ?? []).map((name) => DEPLOY_SECRET_PREFIX + name)
}

export function needs(p: ProviderInfo): string {
  const names = deploySecretNames(p)
  return names.length ? `needs ${names.join(' + ')}` : 'a secret is missing on the service'
}

/** `16 kHz in · 24 kHz out`, or `24 kHz audio` when both directions agree. */
export function audioLabel(p: ProviderInfo): string {
  if (!p.audio) return ''
  const khz = (f: AudioFormat) => `${Math.round(f.sampleRate / 1000)} kHz`
  return khz(p.audio.input) === khz(p.audio.output)
    ? `${khz(p.audio.input)} audio`
    : `${khz(p.audio.input)} in · ${khz(p.audio.output)} out`
}

/**
 * Reads the catalog. Accepts `{ providers: [...] }` (what the Worker sends) or a
 * bare array (what SERVICE_API.md shows), and returns `null` — not an error — when
 * the endpoint is missing, so an older service degrades to the saved choice.
 */
export async function fetchCatalog(url: string, fetchFn: typeof fetch = fetch): Promise<ProviderInfo[] | null> {
  let body: unknown
  try {
    const res = await fetchFn(url)
    if (!res.ok) return null
    body = await res.json()
  } catch {
    return null
  }
  return normaliseCatalog(body)
}

export function normaliseCatalog(body: unknown): ProviderInfo[] | null {
  const raw = Array.isArray(body) ? body : isRecord(body) && Array.isArray(body.providers) ? body.providers : null
  if (!raw) return null
  const out: ProviderInfo[] = []
  for (const entry of raw) {
    if (!isRecord(entry) || typeof entry.id !== 'string') continue
    out.push({
      id: entry.id,
      placements: Array.isArray(entry.placements) ? (entry.placements as Placement[]) : ['server'],
      status: entry.status === 'planned' ? 'planned' : 'ready',
      configured: typeof entry.configured === 'boolean' ? entry.configured : undefined,
      missingSecrets: Array.isArray(entry.missingSecrets)
        ? (entry.missingSecrets as unknown[]).filter((n): n is string => typeof n === 'string')
        : undefined,
      summary: typeof entry.summary === 'string' ? entry.summary : undefined,
      audio: isAudioPair(entry.audio) ? entry.audio : undefined,
    })
  }
  return out.length ? out : null
}

/**
 * Which provider to start on when the user has never pinned one.
 *
 * If the service reports configuredness, the first *configured* provider in its own
 * preference order wins — which is what makes "paste a key and talk" true: a key
 * appearing in the Worker's secrets is the whole of switching to that provider.
 *
 * If it does not report it (an older service), every ready provider looks equally
 * usable and preferring the first would silently move a working setup onto something
 * that needs a key. So in that case the saved choice is kept.
 */
export function autoPick(catalog: ProviderInfo[], current: string): string {
  const reportsConfig = catalog.some((p) => p.configured !== undefined)
  if (!reportsConfig) return catalog.some((p) => p.id === current && usable(p)) ? current : (catalog.find(usable)?.id ?? current)
  return catalog.find(usable)?.id ?? current
}

export interface Refusal {
  head: string
  detail: string[]
}

/**
 * What to say when `autoPick` moves an unpinned session off `from` and onto `to` —
 * or `null` for "say nothing", which is the answer on a first visit.
 *
 * `returning` is the whole point. The app's built-in default is not a preference: it
 * is written to storage during boot, so a browser that has never run this before will
 * happily report a "saved provider" the app invented seconds earlier. Announcing that
 * opened a clean first load on a warning about the visitor's own settings. Once there
 * IS a previous choice, a swap is worth a line — naming what it left, and why.
 */
export function swapNotice(
  from: string,
  to: string,
  previous: ProviderInfo | undefined,
  returning: boolean,
): Refusal | null {
  if (from === to || !returning) return null
  return {
    head: `starting on ${to} instead of ${from}`,
    detail: [
      previous && previous.configured === false
        ? `${from} ${needs(previous)}`
        : `the service lists ${to} first among the providers it can serve`,
    ],
  }
}

/**
 * Turns a refused `POST /v1/sessions` into something a person can act on. The
 * service names the missing secret in its own error details, so those are passed
 * through verbatim rather than guessed at here.
 */
export function describeRefusal(
  err: { status: number; message: string; details?: string[] },
  info: ProviderInfo | undefined,
  sessionUrl: string,
): Refusal {
  // The service's own words come first, verbatim: it names the missing secret and how
  // to set it, and paraphrasing that would only lose detail.
  const detail: string[] = [...(err.details ?? [])]
  const secrets = info ? deploySecretNames(info) : []
  if (info?.configured === false && !secrets.some((name) => detail.some((d) => d.includes(name) || d.includes(name.slice(DEPLOY_SECRET_PREFIX.length))))) {
    detail.push(`${info.id} ${needs(info)}`)
  }

  if (err.status === 0) {
    detail.push('is `npm run dev` running in apps/voicebox/server?')
  } else if (err.status === 401) {
    detail.push(`${sessionUrl} needs an API key — in prod that is the Pages broker's VOICEBOX_API_KEY`)
  } else if (err.status === 502) {
    detail.push('the service reached the provider but could not open a session on it')
  }
  return { head: `could not start a session — ${err.message}`, detail }
}

function isAudioPair(v: unknown): v is { input: AudioFormat; output: AudioFormat } {
  return isRecord(v) && isRecord(v.input) && isRecord(v.output) && typeof v.input.sampleRate === 'number'
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}
